"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Calendar, User } from "lucide-react";
import { SimpleCompanyBadge } from "../../entities/SimpleCompanyBadge";
import { formatCurrency, formatDate } from "../../utils/invoiceFormatters";
import type { InvoiceDisplayItem } from "@/lib/utils/crm/quickbooks-company-ref";

interface InvoiceCardProps {
  invoice: InvoiceDisplayItem;
  onViewDetails: (invoiceId: string, realmId?: string) => void;
  getInvoiceCustomerName: (invoiceItem: InvoiceDisplayItem) => string;
}

function getStatusBadge(balance?: number, total?: number) {
  if (balance === undefined || total === undefined) {
    return <Badge variant="secondary">Unknown</Badge>;
  }

  if (balance === 0) {
    return (
      <Badge variant="default" className="bg-green-500">
        Paid
      </Badge>
    );
  } else if (balance === total) {
    return <Badge variant="destructive">Unpaid</Badge>;
  } else {
    return <Badge variant="outline">Partially Paid</Badge>;
  }
}

export function InvoiceCard({ invoice, onViewDetails, getInvoiceCustomerName }: InvoiceCardProps) {
  const customerName = getInvoiceCustomerName(invoice);

  return (
    <Card className="shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        {/* Company Badge - Prominent at the top */}
        {invoice.company && (
          <div className="mb-3 -mt-2">
            <SimpleCompanyBadge
              company={invoice.company}
              variant="default"
              size="md"
              className="font-semibold"
            />
          </div>
        )}

        {/* Customer Connection Badge */}
        {customerName && customerName !== invoice.data.CustomerRef?.name && (
          <div className="mb-2">
            <Badge variant="secondary" className="text-xs">
              <User className="h-3 w-3 mr-1" />
              {customerName}
            </Badge>
          </div>
        )}

        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5 text-blue-600" />
            Invoice #{invoice.data.DocNumber || "N/A"}
          </CardTitle>
          <div className="flex items-center gap-2">
            {getStatusBadge(invoice.data.Balance, invoice.data.TotalAmt)}
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span>Date: {formatDate(invoice.data.TxnDate)}</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span>Due: {formatDate(invoice.data.DueDate)}</span>
          </div>
          {customerName && (
            <div className="flex items-center gap-1">
              <User className="h-4 w-4" />
              <span>{customerName}</span>
              {invoice.data.CustomerRef?.value && (
                <span className="text-xs text-muted-foreground ml-1">
                  ({invoice.data.CustomerRef.value})
                </span>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(invoice.data.TotalAmt)}
            </div>
            {invoice.data.Balance !== invoice.data.TotalAmt && (
              <div className="text-sm text-gray-600">
                Balance:{" "}
                <span className="font-semibold text-red-600">
                  {formatCurrency(invoice.data.Balance)}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewDetails(invoice.data.Id ?? "", invoice.company?.realmId)}
            >
              View Details
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
